import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Activity, ArrowLeft, BadgeCheck, CheckCircle2, Download, ExternalLink,
  Loader2, RefreshCw, Smartphone, ShieldCheck, Globe, Mail,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  NABD_FONT_AR, NABD_FONT_DISPLAY, ensureNabdFonts, nabdVisitorId,
} from "../../lib/nabdTheme";

// AED→USD conversion (×0.27 — AED pegged ~3.67 to USD) so TikTok's USD-only
// benchmark aggregates with every other Reply Rescue funnel's reporting.
const NABD_USD = Math.round(99 * 0.27 * 100) / 100;

// Google Ads conversion — same conversion ID & label scheme as the other
// funnels for unified Google Ads reporting. Never throws if gtag absent.
const fireGoogleAdsPurchase = (tid) => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/eSJQCMzv7r4cEJy5q_lD",
      value: 1.0,
      currency: "AED",
      transaction_id: tid || "",
    });
  } catch (_) { /* never breaks */ }
};

const APK_PATH = "/downloads/nabd-market-ai.apk";
const IOS_URL =
  "https://apps.apple.com/us/app/" +
  "%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%A3%D8%B3%D9%87%D9%85-ai/" +
  "id6748657713?l=ar";

// Tracking helper — best-effort, never blocks UX.
const trackNabd = (event, props = {}) => {
  try {
    api.post("/nabd/track", {
      event,
      props,
      visitor_id: nabdVisitorId(),
      page: "success-nabd",
    });
  } catch (_) { /* best-effort */ }
};

export default function SuccessNabd() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const csId = params.get("cs") || "";
  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [err, setErr] = useState("");
  // Guard so the TikTok Purchase pixel fires once per page load even though
  // both the polling effect and the manual reconcile may both reach 'paid'.
  const firedRef = useRef(false);

  // Fire browser-side Purchase the moment we observe the order flip to paid.
  // Uses a deterministic event_id matching the backend webhook (purchase_{cs_id})
  // so TikTok dedupes both signals into a single attributed conversion. Also
  // sends the Google Ads conversion for unified ROI reporting.
  const firePurchase = (o) => {
    if (firedRef.current) return;
    firedRef.current = true;
    try {
      if (window.ttq?.track) {
        const dedupKey = csId || token || "noid";
        const winKey = `__nabd_purchase_${dedupKey}`;
        if (!window[winKey]) {
          window[winKey] = true;
          window.ttq.track(
            "Purchase",
            {
              contents: [{
                content_id: "nabd_market_ai",
                content_type: "product",
                content_name: "نبض السوق AI",
                quantity: 1,
                price: NABD_USD,
              }],
              value: NABD_USD,
              currency: "USD",
            },
            { event_id: `purchase_${dedupKey}` },
          );
        }
      }
    } catch (_) { /* never breaks the success page */ }
    fireGoogleAdsPurchase(csId || token);
  };

  // Poll for paid status — webhook may take a moment after Stripe redirect.
  // If still pending after a few tries we offer manual reconcile.
  useEffect(() => {
    ensureNabdFonts();
    document.title = "نبض السوق AI — تأكيد الطلب";
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/nabd/order/${token}`);
        if (cancelled) return;
        setOrder(data);
        if (data.status === "paid") {
          firePurchase(data);
          setPolling(false);
          return;
        }
      } catch (e) {
        if (!cancelled) setErr("تعذّر تحميل بيانات الطلب");
      }
      if (!cancelled && attempts < 8) setTimeout(tick, 1500);
      else if (!cancelled) setPolling(false);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Manual fallback when webhook delivery is delayed.
  const runReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    setErr("");
    try {
      await api.post(`/nabd/reconcile/${token}`);
      const { data } = await api.get(`/nabd/order/${token}`);
      setOrder(data);
      if (data?.status === "paid") firePurchase(data);
    } catch (e2) {
      setErr("تعذّرت إعادة التحقق — حاول بعد قليل");
    } finally {
      setReconciling(false);
    }
  };

  const paid = order?.status === "paid";

  const handleAndroidDownload = () => {
    trackNabd("android_download", { token });
    // Don't preventDefault — let the browser fetch the file naturally.
  };
  const handleIosOpen = () => {
    trackNabd("ios_open", { token });
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#06090F] text-[#E8ECEF]"
      style={{ fontFamily: NABD_FONT_AR }}
      data-testid="nabd-success-root"
    >
      <nav className="sticky top-0 z-30 bg-[#06090F]/85 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center border" style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.40)" }}>
              <Activity className="w-3.5 h-3.5 text-[#F4B842]" strokeWidth={2.4} />
            </div>
            <span className="font-bold text-[14px] text-white" style={{ fontFamily: NABD_FONT_DISPLAY }}>نبض السوق <span className="text-[#F4B842]">AI</span></span>
          </div>
          <Link to="/nabd" className="text-[12.5px] text-[#6B7480] hover:text-white transition-colors">الرئيسية</Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-5 py-12">
        {/* Status header */}
        <div className="text-center mb-10">
          {paid ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 border" style={{ background: "rgba(0,212,168,0.12)", borderColor: "rgba(0,212,168,0.30)" }}>
                <CheckCircle2 className="w-9 h-9 text-[#00D4A8]" strokeWidth={1.8} />
              </div>
              <div className="text-[10.5px] tracking-[0.30em] text-[#00D4A8] font-bold mb-3">تم تأكيد الدفع</div>
              <h1
                className="font-extrabold text-[28px] sm:text-[36px] leading-[1.25] text-white mb-3"
                style={{ fontFamily: NABD_FONT_DISPLAY }}
                data-testid="nabd-success-title"
              >
                مرحبًا في نبض السوق AI.
              </h1>
              <p className="text-[14px] text-[#9AA5B1] leading-[2] max-w-md mx-auto">
                التطبيق جاهز للتنزيل. اختر نظامك في الأسفل وتابع التعليمات السريعة.
              </p>
            </>
          ) : polling ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 border" style={{ background: "rgba(244,184,66,0.12)", borderColor: "rgba(244,184,66,0.30)" }}>
                <Loader2 className="w-8 h-8 text-[#F4B842] animate-spin" strokeWidth={1.8} />
              </div>
              <h1 className="font-extrabold text-[24px] sm:text-[30px] leading-[1.3] text-white mb-3" style={{ fontFamily: NABD_FONT_DISPLAY }}>
                جارٍ تأكيد الدفع…
              </h1>
              <p className="text-[13.5px] text-[#9AA5B1] leading-[2] max-w-md mx-auto">
                نتحقق من Stripe الآن. هذا يستغرق بضع ثوانٍ فقط.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 border" style={{ background: "rgba(239,107,107,0.10)", borderColor: "rgba(239,107,107,0.30)" }}>
                <RefreshCw className="w-8 h-8 text-[#EF6B6B]" strokeWidth={1.8} />
              </div>
              <h1 className="font-extrabold text-[24px] sm:text-[30px] leading-[1.3] text-white mb-3" style={{ fontFamily: NABD_FONT_DISPLAY }}>
                لم نستلم تأكيد الدفع بعد.
              </h1>
              <p className="text-[13.5px] text-[#9AA5B1] leading-[2] max-w-md mx-auto mb-5">
                إذا أتممت الدفع للتو، اضغط الزر أدناه للتحقق يدويًا.
              </p>
              <button
                onClick={runReconcile}
                disabled={reconciling}
                data-testid="nabd-reconcile-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-[13px] text-[#F4B842] border transition-colors disabled:opacity-70"
                style={{ background: "rgba(244,184,66,0.08)", borderColor: "rgba(244,184,66,0.35)" }}
              >
                {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                تحقق من الدفع
              </button>
              {err && <div className="mt-3 text-[12px] text-[#EF6B6B]">{err}</div>}
            </>
          )}
        </div>

        {paid && (
          <>
            {/* Two download options — Android primary, iOS App Store secondary */}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="nabd-download-options">
              <a
                href={APK_PATH}
                onClick={handleAndroidDownload}
                download
                data-testid="nabd-android-download"
                className="group block rounded-2xl p-6 border transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(0,212,168,0.30)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ background: "rgba(0,212,168,0.10)", borderColor: "rgba(0,212,168,0.30)" }}>
                    <Smartphone className="w-5 h-5 text-[#00D4A8]" />
                  </div>
                  <div>
                    <div className="text-[10.5px] tracking-[0.22em] text-[#00D4A8] font-bold">Android</div>
                    <div className="font-bold text-[15px] text-white">تنزيل التطبيق</div>
                  </div>
                </div>
                <p className="text-[12.5px] text-[#9AA5B1] leading-[1.85] mb-4">
                  تنزيل مباشر للملف. ستظهر تعليمات التثبيت أسفل الصفحة.
                </p>
                <div className="inline-flex items-center gap-2 text-[13px] font-bold text-[#00D4A8]">
                  <Download className="w-4 h-4" />
                  تنزيل الآن
                </div>
              </a>

              <a
                href={IOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleIosOpen}
                data-testid="nabd-ios-open"
                className="group block rounded-2xl p-6 border transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.30)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ background: "rgba(244,184,66,0.10)", borderColor: "rgba(244,184,66,0.35)" }}>
                    <Globe className="w-5 h-5 text-[#F4B842]" />
                  </div>
                  <div>
                    <div className="text-[10.5px] tracking-[0.22em] text-[#F4B842] font-bold">iPhone · iPad</div>
                    <div className="font-bold text-[15px] text-white">فتح App Store</div>
                  </div>
                </div>
                <p className="text-[12.5px] text-[#9AA5B1] leading-[1.85] mb-4">
                  افتح صفحة التطبيق على App Store وثبّته من جوالك مباشرة.
                </p>
                <div className="inline-flex items-center gap-2 text-[13px] font-bold text-[#F4B842]">
                  <ExternalLink className="w-4 h-4" />
                  فتح App Store
                </div>
              </a>
            </div>

            {/* Android install help */}
            <div
              className="mt-6 rounded-2xl p-5 border"
              style={{ background: "#0B1018", borderColor: "rgba(255,255,255,0.05)" }}
              data-testid="nabd-android-help"
            >
              <div className="text-[10.5px] tracking-[0.22em] text-[#F4B842] font-bold mb-3">طريقة تثبيت Android</div>
              <ol className="space-y-2 text-[13px] text-[#E8ECEF] leading-[1.85]">
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[rgba(244,184,66,0.12)] text-[#F4B842] flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">١</span>
                  اضغط على «تنزيل الآن» في الأعلى وانتظر اكتمال التنزيل.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[rgba(244,184,66,0.12)] text-[#F4B842] flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">٢</span>
                  افتح ملف التنزيل. إذا ظهرت رسالة، اسمح بالتثبيت من مصادر خارجية.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[rgba(244,184,66,0.12)] text-[#F4B842] flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5">٣</span>
                  اضغط «تثبيت»، ثم افتح التطبيق وابدأ بضبط اهتماماتك الاستثمارية.
                </li>
              </ol>
            </div>

            {/* Receipt / Email confirmation note */}
            <div className="mt-5 flex items-start gap-3 rounded-xl px-4 py-3 border" style={{ background: "rgba(0,212,168,0.05)", borderColor: "rgba(0,212,168,0.22)" }}>
              <Mail className="w-4 h-4 text-[#00D4A8] mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-[#E8ECEF] leading-[1.85]">
                {order?.email
                  ? <>أُرسل إيصال الدفع إلى <span className="font-bold text-white" dir="ltr">{order.email}</span> عبر Stripe.</>
                  : "أُرسل إيصال الدفع عبر Stripe إلى البريد الذي أدخلته في صفحة الدفع."}
              </div>
            </div>
          </>
        )}

        {/* Order meta */}
        {order && (
          <div className="mt-8 rounded-xl p-4 border text-[11.5px] text-[#6B7480]" style={{ background: "#040608", borderColor: "rgba(255,255,255,0.05)" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span>رقم الطلب: <span className="text-[#E8ECEF] font-mono" dir="ltr">{token?.slice(0, 12)}…</span></span>
              <span className="inline-flex items-center gap-1">
                <BadgeCheck className="w-3 h-3 text-[#F4B842]" />
                {order.amount} {order.currency}
              </span>
            </div>
          </div>
        )}
      </main>

      <footer className="py-8 border-t border-white/[0.05] text-center px-5 bg-[#040608]">
        <p className="text-[11px] text-[#404853] max-w-xl mx-auto leading-relaxed inline-flex items-center gap-2">
          <ShieldCheck className="w-3 h-3 text-[#F4B842]" />
          نبض السوق AI أداة معلومات وتحليل — لا يقدم توصيات استثمارية مباشرة.
        </p>
      </footer>
    </div>
  );
}
